import { ExporterErrorCode, SaveType } from '@tencent/mega-sdk-protocols';
import { ISlideInitialData } from '../../../core/interfaces/slide-initial-data-service.interface';
import { ILogger } from '../../../core/slide-data-services.core';
import { ILocalEditContext } from '../core/interfaces/local-edit-context.interface';
import { ILocalEditDataConverter } from '../core/interfaces/local-edit-data-converter.interface';
import { ILocalEditEvents, LocalEditEvents } from '../core/interfaces/local-edit-events.interface';
import { ILocalEditService } from '../core/interfaces/local-edit-service.interface';
import { createTimestampGenerator, formatError } from '../../../utils/trace-format';

const MODULE = '[LocalEditService]';

export class LocalEditService implements ILocalEditService {
    public constructor(
        private logger: ILogger,
        private context: ILocalEditContext,
        private emitter: ILocalEditEvents,
        private converter: ILocalEditDataConverter,
    ) {
        this.logger.info(MODULE, '[init]', `converter=${converter.name}`);
        this.emitter.emit(LocalEditEvents.LocalEditServiceInit);
    }

    /**
     * 获取桌面端本地打开文档的内容数据
     * 数据异常则返回null，后续展示默认空ppt
     */
    public loadDocument = async (): Promise<ISlideInitialData> => {
        const timestamp = createTimestampGenerator();
        const title = this.context.getTitle();
        this.logger.info(MODULE, '[load:begin]', title, timestamp());
        this.emitter.emit(LocalEditEvents.LoadDocumentStart);
        return this.converter.loadPresentation(
            {
                globalPadId: this.context.getGlobalPadId(),
                localFilePath: this.context.getLocalFilePath(),
                recoveredType: this.context.getRecoveredType(),
                templatePath: this.context.getTemplatePath(),
            },
            {
                onHead: (total) => {
                    this.logger.info(MODULE, '[load:head]', title, `[1/${total}]`, timestamp());
                    this.emitter.emit(LocalEditEvents.LoadDocumentChunkReceived, 0, total);
                    this.emitter.emit(LocalEditEvents.LoadDocumentHeadSuccess);
                },
                onChunk: (index, total) => {
                    this.logger.info(MODULE, '[load:chunk]', title, `[${index + 1}/${total}]`, timestamp());
                    this.emitter.emit(LocalEditEvents.LoadDocumentChunkReceived, index, total);
                },
                onEnd: () => {
                    this.logger.info(MODULE, '[load:end]', title, timestamp());
                    this.emitter.emit(LocalEditEvents.LoadDocumentAllChunksSuccess);
                },
                onFatalError: (error) => {
                    this.logger.error(MODULE, '[load:error]', title, formatError(error), timestamp());
                    this.emitter.emit(LocalEditEvents.LoadDocumentError, error);
                },
            },
        );
    };

    public async saveDocument(data: string, saveType: SaveType, isExport?: boolean): Promise<void> {
        const timestamp = createTimestampGenerator();
        const beginAt = Date.now();
        const title = await this.context.getSaveTitle();
        this.logger.info(MODULE, '[save:begin]', title, timestamp());

        return new Promise((resolve, reject) => {
            const traceKey = this.context.createTraceKey();
            this.emitter.emit(LocalEditEvents.SaveDocumentBegin, traceKey);

            this.converter
                .savePresentation(
                    {
                        globalPadId: this.context.getGlobalPadId(),
                        localFilePath: this.context.getLocalFilePath(),
                        title,
                        data,
                        saveType,
                        traceKey,
                        recoveredType: this.context.getRecoveredType(),
                    },
                    {
                        onDocumentSaved: (newPath, newTitle, newGlobalPadId) => {
                            this.logger.info(MODULE, '[save:success]', title, timestamp());
                            // 文档另存为新路径，或者历史缓存版本另存为(无论是否覆盖原有文件)
                            const isSaveAs = newPath !== this.context.getLocalFilePath()
                                || this.context.getRecoveredType();
                            if (newTitle && newPath && isSaveAs && !isExport) {
                                this.emitter.emit(
                                    LocalEditEvents.DocumentInfoChanged,
                                    newPath,
                                    newTitle,
                                    newGlobalPadId = this.context.getGlobalPadId(),
                                );
                            }
                            // 修改完 url 参数后再 emit 事件，确保上报时能获取最新参数
                            this.emitter.emit(LocalEditEvents.SaveDocumentSuccess, saveType, traceKey, beginAt);
                            resolve();
                        },
                        onCancel: () => {
                            this.logger.error(MODULE, '[save:cancel]', title, timestamp());
                            reject({
                                name: 'CancelError',
                                message: 'convert process is canceled',
                                code: ExporterErrorCode.kExportCancel,
                            });
                        },
                        onAbort: () => {
                            this.logger.error(MODULE, '[save:abort]', title, timestamp());
                            reject({
                                name: 'AbortError',
                                message: 'convert process is aborted',
                                code: ExporterErrorCode.kExportCancel,
                            });
                        },
                        onFatalError: (error) => {
                            this.logger.error(MODULE, '[save:error]', title, formatError(error), timestamp());
                            this.emitter.emit(LocalEditEvents.SaveDocumentError, error, saveType, traceKey);
                            reject(error);
                        },
                    },
                )
                .catch(reject);

            // 桌面端自动保存文档数据不关心结果，直接返回成功
            if (saveType === SaveType.AutoSave) {
                resolve();
            }
        });
    }
}
